import { whyNotReschedulable } from '@servwell/shared';
import { MORNING_ENDS_AT } from '../../lib/format';
import { hoursMinutes } from './visitTiming';

/**
 * The next-stop moment, as pure functions (117, tech half — Purrweb ref).
 *
 * ⭐ Pure and separate from the card for the same reason `visitTiming.ts` is: `apps/web`
 * runs its tests in node, so the arithmetic deciding WHICH stop a tech is told to drive to
 * — and how long they have — is the half a test can hold to account. What is left in the
 * component is markup and a timer.
 *
 * 🔴 **A countdown is a DURATION, and durations have no timezone.** The gap between two
 * instants is the same number of minutes in Springfield and in Berlin, so nothing in this
 * module takes a `tz`. That is deliberate and worth stating, because the file next door
 * (`TechTodayPage`) carries a warning about three private helpers that read the BROWSER's
 * clock and put a van on the wrong day. The zone still decides WHICH DAY is today — that
 * stays with `todayISO(tz)` where it already lives — but it must not be smuggled in here,
 * where it would look like diligence and do nothing.
 *
 * ⚠️ **What this cannot fix: a phone whose CLOCK is wrong.** `now` is the device's, so a
 * handset 20 minutes fast shows a countdown 20 minutes short. Correcting it needs a server
 * time offset the app does not have. The card's mitigation is to print the scheduled TIME
 * beside the countdown — a tech who can see "9:30 AM" can catch a countdown that disagrees
 * with the clock on the dash, which a bare "in 42m" gives them no way to do.
 */

/** The minimum a caller must be able to say about a stop for this module to rank it. */
export interface StopFacts {
  status: string;
  scheduledStartAt: string | Date | null;
}

/**
 * Stops that are behind you.
 *
 * ⚠️ `canceled` is here for the same reason `rowsForTab`'s `all` tab deliberately KEEPS it:
 * a canceled stop is something the tech must see so they do not drive to it. Seeing it in
 * the list and never being told it is "next" are the same decision from two directions.
 */
const SETTLED = new Set(['completed', 'canceled']);

/**
 * Stops the tech has already committed to — they are on the road, or standing in it.
 *
 * ⭐ These OUTRANK an earlier scheduled stop. A tech who taps "On my way" at 2:10 for a
 * 2:30 job, while a 1:00 job sits un-started in the list because the customer was out, must
 * not be told the 1:00 is next: they are in the van heading somewhere else. Sorting by time
 * alone gets this exactly backwards, and it gets it backwards on the worst day.
 */
const COMMITTED = new Set(['en_route', 'in_progress']);

const at = (v: string | Date | null): number | null => {
  if (v === null) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * The one stop the card is about, or null when there is nothing left to drive to.
 *
 * The order is: committed first, then the earliest scheduled — including one whose time has
 * already PASSED, because a tech running an hour behind still has that stop next. Ranking
 * only future stops would blank the card at exactly the moment it is most use.
 *
 * ⚠️ **A stop with no `scheduledStartAt` can still be next**, and it sorts last among its
 * own group. `myDay`'s window is `scheduled_start_at >= from` so today's rows all have one
 * in practice — but the column is nullable, the sort in `TechTodayPage` coerces null to `0`
 * (epoch, i.e. first), and inheriting that here would have made an untimed stop outrank
 * every real one. The card reads `startsAt: null` as "no countdown", never as 1970.
 */
export function pickNextStop<T>(
  rows: readonly T[],
  facts: (row: T) => StopFacts,
): { row: T; startsAt: number | null } | null {
  const open = rows.filter((r) => !SETTLED.has(facts(r).status));
  if (open.length === 0) return null;

  const rank = (r: T) => (COMMITTED.has(facts(r).status) ? 0 : 1);
  const best = open.reduce((a, b) => {
    if (rank(a) !== rank(b)) return rank(a) < rank(b) ? a : b;
    const ta = at(facts(a).scheduledStartAt);
    const tb = at(facts(b).scheduledStartAt);
    // Null sorts LAST within a group — never to the epoch, which would win every race.
    if (ta === null) return tb === null ? a : b;
    if (tb === null) return a;
    return ta <= tb ? a : b;
  });

  return { row: best, startsAt: at(facts(best).scheduledStartAt) };
}

/**
 * How the gap to a stop reads, as a state and a magnitude.
 *
 * ⚠️ **`late` is a first-class state, not a negative number.** The reference draws a
 * countdown and stops there; a real day runs behind, and a countdown that keeps subtracting
 * renders "in -12m" — which is both ugly and, worse, easy to misread as time remaining.
 * Splitting the sign out here means the component never formats a negative.
 */
export type CountdownState = 'unknown' | 'now' | 'ahead' | 'late';

export interface Countdown {
  state: CountdownState;
  /** Whole minutes to (or past) the start. Always >= 0 — the sign lives in `state`. */
  minutes: number;
}

/**
 * ⚠️ The "now" band is ±1 minute and it is SYMMETRIC. A stop 30 seconds away and one 30
 * seconds overdue are the same fact to the person driving to it, and flipping from "in 1m"
 * to "1m late" across a single tick would make the card look broken rather than precise.
 */
export function countdown(startsAt: number | null, now: number): Countdown {
  if (startsAt === null) return { state: 'unknown', minutes: 0 };
  const deltaMs = startsAt - now;
  const minutes = Math.abs(Math.round(deltaMs / 60_000));
  if (minutes < 1) return { state: 'now', minutes: 0 };
  return { state: deltaMs > 0 ? 'ahead' : 'late', minutes };
}

/**
 * The countdown in words: "in 42m", "in 2h 10m", "now", "12m late".
 *
 * Returns null when there is nothing to say — an untimed stop gets the rest of the card and
 * no invented number.
 */
export function countdownLabel(c: Countdown): string | null {
  switch (c.state) {
    case 'unknown':
      return null;
    case 'now':
      return 'now';
    case 'ahead':
      return `in ${hoursMinutes(c.minutes)}`;
    case 'late':
      return `${hoursMinutes(c.minutes)} late`;
  }
}

/**
 * The promised window versus the time the stop is actually booked for — in words, or null
 * when there is nothing wrong.
 *
 * 🔴 **The defect this closes is one the next-stop card itself can CAUSE.** "Running behind"
 * moves a stop forward and does not touch `visits.window`, so a +1h on an 11:30 morning call
 * lands at 12:30 and the card read **"12:30 PM · Morning"** — a promise and a time that
 * contradict each other, printed side by side with no sign anything was wrong. Nudging is
 * not the only way in; the office's board can drag a visit across noon too.
 *
 * ⭐ It REPORTS rather than hides. Suppressing the window would make the contradiction go
 * away on screen and leave it in force with the customer, who was told a morning. The tech
 * is the person who can still ring ahead, so the tech is told.
 *
 * ⚠️ `minutesIntoDay` is minutes since midnight **at the shop** (`minutesInTz`), never the
 * browser's — a van working a state line would otherwise cross noon an hour early or late.
 *
 * `exact` and `all_day` can never be missed: one promises an instant, which the countdown
 * already speaks to, and the other promises the whole day.
 */
export function windowNotice(
  window: string,
  minutesIntoDay: number | null,
): string | null {
  if (minutesIntoDay === null) return null;
  if (window === 'am' && minutesIntoDay >= MORNING_ENDS_AT)
    return 'Morning window missed';
  // The same one boundary read from the other side — an afternoon call booked before noon is
  // equally not what the customer was told. Reachable from the office's board, not from a
  // nudge, which only ever moves a stop later.
  if (window === 'pm' && minutesIntoDay < MORNING_ENDS_AT) {
    return 'Before the afternoon window';
  }
  return null;
}

/**
 * The nudges "Running behind" offers, in minutes.
 *
 * ⚠️ Relative, never a date picker. The gesture this replaces is a tech thumbing a calendar
 * in a moving van; the question they actually have is "push it a bit", and `visit.reschedule`
 * takes an exact instant, so the arithmetic belongs here where it can be tested rather than
 * in an `onClick`.
 */
export const NUDGE_MINUTES = [15, 30, 60] as const;

/**
 * Where a nudge lands.
 *
 * ⚠️ Measured from the SCHEDULED start, not from `now`. Nudging a stop that is already 40
 * minutes late by "+15m" must move it to 15 minutes after the time the office booked — a
 * quarter hour from *now* would silently absorb the 40 minutes of lateness into the new
 * promise and tell the customer a time the tech has no reason to believe.
 *
 * Returns null when there is no start to measure from — the caller hides the control rather
 * than sending `reschedule` an instant it invented.
 */
export function nudgedStart(startsAt: number | null, minutes: number): Date | null {
  if (startsAt === null) return null;
  return new Date(startsAt + minutes * 60_000);
}

/**
 * Whether this stop can be moved from the card at all.
 *
 * 🔴 **Asks the SERVER's own predicate rather than restating its rule.**
 * `whyNotReschedulable` is in `@servwell/shared` precisely so both sides read one copy: it
 * refuses `en_route` ("a tech is already on the way") and `in_progress` ("already on site").
 * Writing `status === 'scheduled'` here would be a second opinion that compiles — and the
 * day the machine gains a sixth state, the card would offer a button whose only possible
 * outcome is a refusal. That is the shape design-language §7 calls copy naming a state the
 * system is not in.
 */
export function canNudge(status: string): boolean {
  return whyNotReschedulable(status) === null;
}
